import { useState, useEffect } from 'react';
import { X, Save, User, Phone, Calendar, Shirt, Hash } from 'lucide-react';
import type { Player, PlayerDetails } from '../types';

interface PlayerEditModalProps {
  player: Player | null;  // null for create mode
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: { name: string; mobile_number: string; details: Partial<PlayerDetails> }) => Promise<boolean>;
  onDelete?: (playerId: string) => Promise<boolean>;
}

const GENDER_OPTIONS = ['Male', 'Female'];
const SIDE_OPTIONS = ['Right', 'Left', 'Both'];
const SIZE_OPTIONS = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];

const PlayerEditModal = ({ player, isOpen, onClose, onSave, onDelete }: PlayerEditModalProps) => {
  const [name, setName] = useState('');
  const [mobile, setMobile] = useState('');
  const [gender, setGender] = useState('');
  const [side, setSide] = useState('');
  const [dob, setDob] = useState('');
  const [jerseyName, setJerseyName] = useState('');
  const [jerseyNumber, setJerseyNumber] = useState('');
  const [jerseySize, setJerseySize] = useState('');
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const isEditMode = !!player;

  // Reset form when modal opens/closes or player changes
  useEffect(() => {
    if (isOpen && player) {
      // Edit mode - populate form with player data
      setName(player.details?.player_full_name || player.name || '');
      setMobile(player.mobile_number || '');
      setGender(player.details?.player_gender || '');
      setSide(player.details?.playing_side || '');
      setDob(player.details?.player_dob || player.details?.date_of_birth || '');
      setJerseyName(player.details?.jersey_name || '');
      setJerseyNumber(player.details?.jersey_number || '');
      setJerseySize(player.details?.jersey_size || player.details?.tshirt_size || '');
    } else if (isOpen) {
      // Create mode - reset form
      setName('');
      setMobile('');
      setGender('');
      setSide('');
      setDob('');
      setJerseyName('');
      setJerseyNumber('');
      setJerseySize('');
    }
    setShowDeleteConfirm(false);
  }, [isOpen, player]);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (!name.trim()) {
      alert('Player name is required');
      return;
    }
    if (!mobile.trim()) {
      alert('Mobile number is required');
      return;
    }

    setSaving(true);
    const success = await onSave({
      name: name.trim(),
      mobile_number: mobile.trim(),
      details: {
        player_full_name: name.trim(),
        player_gender: gender || undefined,
        playing_side: side || undefined,
        player_dob: dob || undefined,
        jersey_name: jerseyName.trim() || undefined,
        jersey_number: jerseyNumber.trim() || undefined,
        jersey_size: jerseySize || undefined
      }
    });
    setSaving(false);

    if (success) {
      onClose();
    }
  };

  const handleDelete = async () => {
    if (!player || !onDelete) return;
    setSaving(true);
    const success = await onDelete(player.id);
    setSaving(false);
    if (success) {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto animate-in fade-in zoom-in duration-200">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200 sticky top-0 bg-white rounded-t-xl">
          <h3 className="text-xl font-bold text-slate-800">
            {isEditMode ? 'Edit Player' : 'Add New Player'}
          </h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Form */}
        <div className="p-4 space-y-4">
          {/* Name */}
          <div>
            <label className="flex items-center text-sm font-medium text-slate-700 mb-1">
              <User className="w-4 h-4 mr-1.5" />
              Full Name <span className="text-red-500 ml-1">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full border border-slate-300 rounded-lg p-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Enter player name"
            />
          </div>

          {/* Mobile */}
          <div>
            <label className="flex items-center text-sm font-medium text-slate-700 mb-1">
              <Phone className="w-4 h-4 mr-1.5" />
              Mobile Number <span className="text-red-500 ml-1">*</span>
            </label>
            <input
              type="text"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              className="w-full border border-slate-300 rounded-lg p-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Enter mobile number"
              disabled={isEditMode}  // Can't change mobile in edit mode
            />
            {isEditMode && (
              <p className="text-xs text-slate-500 mt-1">Mobile number cannot be changed</p>
            )}
          </div>

          {/* Gender & Playing Side - Row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Gender</label>
              <select
                value={gender}
                onChange={(e) => setGender(e.target.value)}
                className="w-full border border-slate-300 rounded-lg p-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">Select...</option>
                {GENDER_OPTIONS.map(g => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Playing Side</label>
              <select
                value={side}
                onChange={(e) => setSide(e.target.value)}
                className="w-full border border-slate-300 rounded-lg p-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">Select...</option>
                {SIDE_OPTIONS.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          </div>

          {/* DOB */}
          <div>
            <label className="flex items-center text-sm font-medium text-slate-700 mb-1">
              <Calendar className="w-4 h-4 mr-1.5" />
              Date of Birth
            </label>
            <input
              type="date"
              value={dob}
              onChange={(e) => setDob(e.target.value)}
              className="w-full border border-slate-300 rounded-lg p-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          {/* Jersey Name & Number - Row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="flex items-center text-sm font-medium text-slate-700 mb-1">
                <Shirt className="w-4 h-4 mr-1.5" />
                Jersey Name
              </label>
              <input
                type="text"
                value={jerseyName}
                onChange={(e) => setJerseyName(e.target.value)}
                className="w-full border border-slate-300 rounded-lg p-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Name on jersey"
              />
            </div>
            <div>
              <label className="flex items-center text-sm font-medium text-slate-700 mb-1">
                <Hash className="w-4 h-4 mr-1.5" />
                Jersey Number
              </label>
              <input
                type="text"
                value={jerseyNumber}
                onChange={(e) => setJerseyNumber(e.target.value)}
                className="w-full border border-slate-300 rounded-lg p-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Number"
              />
            </div>
          </div>

          {/* Jersey Size */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Jersey Size</label>
            <select
              value={jerseySize}
              onChange={(e) => setJerseySize(e.target.value)}
              className="w-full border border-slate-300 rounded-lg p-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">Select size...</option>
              {SIZE_OPTIONS.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          {/* Manual Edit Notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
            <strong>Note:</strong> Manually edited players will not be overwritten during WhatsApp sync.
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-slate-200 bg-slate-50 rounded-b-xl">
          {/* Delete button (only in edit mode for unsold players) */}
          <div>
            {isEditMode && player?.status !== 'sold' && onDelete && (
              <>
                {!showDeleteConfirm ? (
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    disabled={saving}
                  >
                    Delete Player
                  </button>
                ) : (
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-red-600">Confirm delete?</span>
                    <button
                      onClick={handleDelete}
                      className="px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700"
                      disabled={saving}
                    >
                      Yes, Delete
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(false)}
                      className="px-3 py-1.5 border border-slate-300 rounded-lg hover:bg-slate-100"
                      disabled={saving}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Save/Cancel buttons */}
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-100 transition-colors"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !name.trim() || !mobile.trim()}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 transition-colors"
            >
              {saving ? (
                <>
                  <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>{isEditMode ? 'Update' : 'Create'} Player</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlayerEditModal;

